import React from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '../../hooks/useScrollReveal';
import SectionHeader from '../ui/SectionHeader';

interface WorkflowStep {
  num: string;
  icon: string;
  title: string;
  desc: string;
}

const steps: WorkflowStep[] = [
  {
    num: '01',
    icon: '📥',
    title: 'Configure',
    desc: 'Map your regulatory obligations, entity structure, and material topics. Actula builds your data collection scope from your specific frameworks — not a generic template.',
  },
  {
    num: '02',
    icon: '⚙️',
    title: 'Collect',
    desc: 'Structured workflows with named data owners. Submissions tracked, reviewers assigned, approval status visible in real time. No chasing spreadsheets by email.',
  },
  {
    num: '03',
    icon: '📊',
    title: 'Calculate',
    desc: 'Carbon calculations, Taxonomy KPIs, CBAM declarations — computed automatically from the data your teams collect. Once. Used everywhere across all five modules.',
  },
  {
    num: '04',
    icon: '✅',
    title: 'Report',
    desc: 'Audit-ready exports in the formats regulators require. Every submission, revision, and approval logged with identity, timestamp, and status.',
  },
];

export default function SolutionWorkflow(): React.ReactElement {
  const ref = useScrollReveal();

  return (
    <section
      className="workflow section-pad"
      id="workflow"
      ref={ref as React.RefObject<HTMLElement>}
    >
      <div className="container">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <img src="/images/actula-logo.png" alt="Actula" style={{ height: '56px', width: 'auto' }} />
        </div>
        <SectionHeader
          title="One platform for every ESG obligation your enterprise faces."
          description="Actula handles the full data journey — framework configuration, structured data collection, carbon calculation, workflow management, multi-entity consolidation, and audit-ready export."
        />

        <div className="workflow-steps">
          {steps.map((s, i) => (
            <div key={i} className={`workflow-step reveal reveal-delay-${i + 1}`}>
              <div className="workflow-step-number">{s.num}</div>
              <span className="workflow-step-icon">{s.icon}</span>
              <p className="workflow-step-title">{s.title}</p>
              <p className="workflow-step-desc">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="workflow-callout-teal reveal">
          <span className="workflow-callout-icon">🗄️</span>
          <div>
            <p className="workflow-callout-title">One data model. No duplicate collection.</p>
            <p className="workflow-callout-text">
              Supplier carbon data collected for CBAM feeds your Scope&nbsp;3 Category&nbsp;1
              calculation and your product carbon footprints &mdash; automatically. The same data
              request, answered once, serves CBAM, CCF, and PCF simultaneously. That is the Actula
              difference.
            </p>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <Link to="/platform" className="link-arrow">See full platform details</Link>
        </div>
      </div>
    </section>
  );
}
